import { useSnapshot } from 'valtio';

import { BrowseFeedbackSentiment } from '@cartwhl/common/api/postBrowseFeedback';
import postBrowseFeedback from '@cartwhl/common/api/postBrowseFeedback';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import { BrowseResultItem, Feedback } from '@cartwhl/common/types';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import DownvoteFullIcon from '@cartwhl/ui/icons/DownvoteFullIcon';
import DownvoteIcon from '@cartwhl/ui/icons/DownvoteIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import ShareIcon from '@cartwhl/ui/icons/ShareIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import TrimIcon from '@cartwhl/ui/icons/TrimIcon';
import UpvoteFullIcon from '@cartwhl/ui/icons/UpvoteFullIcon';
import UpvoteIcon from '@cartwhl/ui/icons/UpvoteIcon';
import Button from '@cartwhl/ui/input/Button';
import Heart from '@cartwhl/ui/messaging/Heart';

import {
  animationGenerationStore,
  navigationStore,
  noticesStore,
} from 'stores';

import { useLibraryStore } from '../useLibraryStore';

type Props = {
  motion: BrowseResultItem;
};

export default function BrowseSearchPreviewMoreMenu({ motion }: Props) {
  const {
    positives,
    negatives,
    favorites,
    addPositiveFeedback,
    addNegativeFeedback,
    addToFavorites,
    removeFromFavorites,
  } = useLibraryStore();
  const { modal } = useSnapshot(navigationStore.state);
  const { jobData } = useSnapshot(animationGenerationStore.state);

  async function handleClickFavorite() {
    const shouldAddToFavorites = !favorites.has(motion.jobID);
    if (shouldAddToFavorites) {
      addToFavorites(motion.jobID);
    } else {
      removeFromFavorites(motion.jobID);
    }

    const result = await animationGenerationStore.setFavorite(
      motion.jobID,
      0,
      shouldAddToFavorites,
      true,
      false,
    );

    if (result?.success) {
      noticesStore.success(
        `${motion.title} ${shouldAddToFavorites ? 'added to' : 'removed from'} favorites!`,
      );
    }
    if (shouldAddToFavorites) {
      const { error } = await postBrowseFeedback({
        jobID: motion.jobID,
        sentiment: BrowseFeedbackSentiment.HEARTS,
        reason: '',
      });
      if (error) {
        console.error(error);
      }
    }
  }

  const isBrowseOpen = modal.type === StudioModalName.BROWSE;

  const setIsBrowseOpen = (v: boolean) => {
    if (v) {
      navigationStore.setModal(StudioModalName.BROWSE, {
        prefillSearchTerm: animationGenerationStore.state.prompt,
        browseItemJobId: motion.jobID,
      });
    } else {
      navigationStore.closeModal();
    }
  };

  return (
    <Dropdown
      side="top"
      trigger={
        <Button
          variant="secondary"
          size="sm"
          className="pointer-events-auto"
          icon={<ThreeDotsIcon />}
        />
      }
    >
      {jobData && (
        <Dropdown.Item
          icon={<PlusIcon />}
          onClick={async () => {
            await animationGenerationStore.submitJobFromExistingMotion({
              currentAnimationNumber:
                animationGenerationStore.state.currentAnimationNumber,
              existingMotionAnimationNumber: 0,
              existingMotionJobID: motion.jobID,
              newSession: true,
            });
          }}
        >
          Use as new motion
        </Dropdown.Item>
      )}
      <Dropdown.Item
        icon={<Heart value={favorites.has(motion.jobID)} size="sm" />}
        onClick={handleClickFavorite}
      >
        Favorite
      </Dropdown.Item>
      <Dropdown.Item
        icon={<TrimIcon />}
        onClick={() => {
          setIsBrowseOpen(!isBrowseOpen);
        }}
      >
        Edit
      </Dropdown.Item>
      <Dropdown.Item
        icon={<ShareIcon />}
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
          noticesStore.info('URL Copied to clipboard!');
        }}
      >
        Share
      </Dropdown.Item>
      <Dropdown.Separator />
      <Dropdown.Item
        icon={
          positives.has(motion.jobID) ? (
            <UpvoteFullIcon className="text-green-500" />
          ) : (
            <UpvoteIcon />
          )
        }
        onClick={async () => {
          const payload: Feedback = {
            jobID: motion.jobID,
            animationNumber: '0',
            sentiment: 'positive',
            mp4Path: motion.mp4Path,
            prompt: motion.title,
          };
          navigationStore.setFeedbackModal(payload);
          addPositiveFeedback(motion.jobID);
          const { error } = await postBrowseFeedback({
            jobID: motion.jobID,
            sentiment: BrowseFeedbackSentiment.UPVOTES,
            reason: '',
          });
          if (error) {
            console.error(error);
          }
        }}
      >
        {positives.has(motion.jobID) ? 'Reported as great' : 'Report great'}
      </Dropdown.Item>
      <Dropdown.Item
        icon={
          negatives.has(motion.jobID) ? (
            <DownvoteFullIcon className="text-red-500" />
          ) : (
            <DownvoteIcon />
          )
        }
        onClick={async () => {
          const payload: Feedback = {
            jobID: motion.jobID,
            animationNumber: '0',
            sentiment: 'negative',
            mp4Path: motion.mp4Path,
            prompt: motion.title,
          };
          navigationStore.setFeedbackModal(payload);
          addNegativeFeedback(motion.jobID);
          const { error } = await postBrowseFeedback({
            jobID: motion.jobID,
            sentiment: BrowseFeedbackSentiment.DOWNVOTES,
            reason: '',
          });
          if (error) {
            console.error(error);
          }
        }}
      >
        {negatives.has(motion.jobID) ? 'Reported as broken' : 'Report broken'}
      </Dropdown.Item>
    </Dropdown>
  );
}
